import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import translations from './translations';
import { PrimaryButton, StaticPageShell } from 'feSharedIndex';
import Confetti from 'components/animation/Confetti';
import SpinningWheel from 'components/animation/SpinningWheel';
import './Wichteln.css';

/**
 * Wichteln Component
 * Secret Santa name assigner with URL-based code access
 */
const Wichteln = () => {
  const { code } = useParams();
  const { language } = useTranslation();
  const pageTranslations = translations[language] || translations.en;
  
  // Get translations helper
  const t = (key, defaultValue = '') => {
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    return value || defaultValue || key;
  };

  // Hardcoded names with IDs (1-24)
  const NAMES = [
    { id: 1, name: 'Aamir Ansari' },
    { id: 2, name: 'Pooja Lama' },
    { id: 3, name: 'Ilia Plloca' },
    { id: 4, name: 'Alexander Miller' },
    { id: 5, name: 'Telma Seiler' },
    { id: 6, name: 'Kathrin Schneider' },
    { id: 7, name: 'Paul Eisenkrätzer' },
    { id: 8, name: 'Aizhan Sulaimanova' },
    { id: 9, name: 'Chiara Manfredi' },
    { id: 10, name: 'Kharrin Agustin' },
    { id: 11, name: 'Tamina Fasel' },
    { id: 12, name: 'Niklas Hartmann' },
    { id: 13, name: 'Maria Galateanu' },
    { id: 14, name: 'Maria Diaz' },
    { id: 15, name: 'Nazmul Haque' },
    { id: 16, name: 'Natasya Limbong' },
    { id: 17, name: 'Sarah Karl' },
    { id: 18, name: 'Greta Hoheisel' },
    { id: 19, name: 'Quynh Vo' },
    { id: 20, name: 'Ketevan Putkaradze' },
    { id: 21, name: 'Daniel John' },
    { id: 22, name: 'Joshua Trogisch' },
    { id: 23, name: 'Antonio Latorre' },
    { id: 24, name: 'Florencia Irene' }
  ];

  // Circular matching: Person N → Person (N+1), Person 24 → Person 1
  const getAssignedPerson = (id) => {
    const assignedId = (id % 24) + 1;
    return NAMES.find(person => person.id === assignedId);
  };

  // Get current user from URL code
  const currentUserId = code ? parseInt(code, 10) : null;
  const currentUser = currentUserId ? NAMES.find(p => p.id === currentUserId) : null;
  
  const [assignedPerson, setAssignedPerson] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [error, setError] = useState('');

  // Validate code on mount
  useEffect(() => {
    if (!code) {
      setError(t('errors.noCode', 'No code provided in URL'));
      return;
    }
    
    const codeNum = parseInt(code, 10);
    if (isNaN(codeNum) || codeNum < 1 || codeNum > 24) {
      setError(t('errors.invalidCode', 'Invalid code in URL'));
      return;
    }

    if (!currentUser) {
      setError(t('errors.userNotFound', 'User not found for this code'));
      return;
    }
  }, [code, currentUser, t]);

  const handleSpin = () => {
    if (!currentUserId || hasSpun) return;
    
    // Get assigned person first
    const assigned = getAssignedPerson(currentUserId);
    setAssignedPerson(assigned);
    
    setIsSpinning(true);
    setError('');
    
    // Start confetti after a short delay
    setTimeout(() => {
      setShowConfetti(true);
    }, 1500);
    
    // Hide confetti after animation
    setTimeout(() => {
      setShowConfetti(false);
    }, 6500);
  };

  const handleSpinComplete = () => {
    setIsSpinning(false);
    setHasSpun(true);
  };

  // Get all names for the wheel (excluding current user)
  const wheelNames = NAMES.map(p => p.name);

  if (error && !currentUser) {
    return (
      <StaticPageShell title={t('title', 'Wichteln')} lead="" contentClassName="wichteln-page">
        <div className="wichteln-container">
          <div className="error-message">
            {error}
          </div>
        </div>
      </StaticPageShell>
    );
  }

  return (
    <StaticPageShell
      title={t('title', 'Wichteln')}
      lead={t('subtitle', 'Find out who you are assigned to gift to')}
      contentClassName="wichteln-page"
    >
      {showConfetti && <Confetti />}
      <div className="wichteln-container">
            {currentUser && (
              <div className="user-info">
                <h2 className="user-name">{currentUser.name}</h2>
              </div>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {!hasSpun && !isSpinning && (
              <div className="spin-container">
                <div className="spin-instructions">
                  <p>{t('spin.instruction', 'Click the button below to spin the wheel and reveal your assignment!')}</p>
                </div>
                <div className="spin-actions">
                  <PrimaryButton
                    onClick={handleSpin}
                    className="spin-button"
                  >
                    {t('spin.button', 'Spin the Wheel!')}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {(isSpinning || hasSpun) && (
              <div className="wheel-container">
                <SpinningWheel
                  items={wheelNames}
                  result={assignedPerson?.name || ''}
                  isSpinning={isSpinning}
                  onSpinComplete={handleSpinComplete}
                />
              </div>
            )}

            {hasSpun && assignedPerson && (
              <div className="result-container">
                <div className="result-card reveal-animation">
                  <div className="result-icon">🎁</div>
                  <h2 className="result-title">
                    {t('result.title', 'Your Assignment')}
                  </h2>
                  <div className="result-content">
                    <p className="result-label">
                      {t('result.message', 'You are assigned to gift to:')}
                    </p>
                    <p className="result-name">
                      {assignedPerson.name}
                    </p>
                  </div>
                </div>
              </div>
            )}
      </div>
    </StaticPageShell>
  );
};

export default Wichteln;

